import React, { useEffect, useState } from "react";
import ListingDetails from "ui/pages/customers/ManageBookings/components/ListingDetails";
import { dataProvider } from "data";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../constants/ZoomAttendance.constants";
import { logError } from "utils/error";
import { apiMethods } from "data/api.constants";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import {
  ExlyPeopleIcon,
  ExlyCheckCircleOutlineIcon,
  ExlyStarBorderIcon,
  ExlyHelpOutlineIcon,
  ExlyEventIcon,
} from "./components/ListingHeaderStatsIconWrappers";
import { useSessionParticipantsContext } from "../SessionParticipantsPage/context/SessionParticipantsContext";

export const ListingHeaderStatsSection = ({
  listingName: propListingName,
  coverImage: propCoverImage,
  listingUuid: propListingUuid,
  sessionDetails: propSessionDetails,
  listingType: propListingType,
}) => {
  const context = useSessionParticipantsContext();
  const listingName = propListingName ?? context.listingName;
  const coverImage = propCoverImage ?? context.coverImage;
  const listingUuid = propListingUuid ?? context.listingUuid;
  const sessionDetails = propSessionDetails ?? context.sessionDetails ?? null;
  const listingType = propListingType ?? context.listingType;
  const isDesktop = useDesktopMediaQuery();
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!listingUuid || sessionDetails) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dataProvider
      .custom_request(
        ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_STATS,
        apiMethods.GET,
        { listing_uuid: listingUuid }
      )
      .then((res) => {
        if (res?.data?.attendance_stats) {
          setStatsData(res.data.attendance_stats);
        }
      })
      .catch((err) => {
        logError({
          error: err,
          when: "calling GET_STATS api",
          occuredAt:
            "src/ui/pages/ZoomAttendance/ZoomAttendanceOfListing/components/ListingHeaderStatsSection/index.js",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [listingUuid, sessionDetails]);

  const isSessionView = Boolean(sessionDetails);

  const statsCards = [];

  if (!isSessionView) {
    const total_session_count =
      statsData?.total_session_count ??
      statsData?.total_sessions ??
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const attendance_avg =
      statsData?.attendance_avg ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const perfect_attendance_count =
      statsData?.perfect_attendance_count ??
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const highly_engaged_count =
      statsData?.highly_engaged_count ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const total_attendees_count =
      statsData?.total_attendees_count ??
      statsData?.total_attendees ??
      statsData?.unrecognized_count ??
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;

    statsCards.push(
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOTAL_SESSIONS_WITH_ATTENDEES,
        statsNumber: total_session_count.toString(),
        Icon: ExlyEventIcon,
        color: "primary",
        tooltipString:
          ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS
            .TOTAL_SESSIONS_WITH_ATTENDEES,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.OVERALL_ATTENDANCE,
        statsNumber: `${Math.round(attendance_avg)}%`,
        Icon: ExlyPeopleIcon,
        color: "persian_blue",
        tooltipString:
          ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.OVERALL_ATTENDANCE,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.PERFECT_ATTENDANCE,
        statsNumber: perfect_attendance_count.toString(),
        Icon: ExlyCheckCircleOutlineIcon,
        color: "fine_pine",
        tooltipString:
          ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.PERFECT_ATTENDANCE,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.HIGHLY_ENGAGED,
        statsNumber: highly_engaged_count.toString(),
        Icon: ExlyStarBorderIcon,
        color: "butterscotch_yellow",
        tooltipString: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.HIGHLY_ENGAGED,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOTAL_ATTENDEES,
        statsNumber: total_attendees_count.toString(),
        Icon: ExlyPeopleIcon,
        color: "coral_red",
        tooltipString:
          ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.TOTAL_ATTENDEES,
      }
    );
  } else {
    const booked_attendees =
      sessionDetails?.total_unique_customers ??
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const attendance_pct =
      sessionDetails?.attendance_percentage ??
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const full_attendance =
      sessionDetails?.full_attendance ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const absent_count =
      sessionDetails?.absent ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    const unrecognized_count =
      sessionDetails?.unrecognized ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;

    statsCards.push(
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.BOOKED_ATTENDEES_TITLE,
        statsNumber: booked_attendees.toString(),
        Icon: ExlyEventIcon,
        color: "primary",
        tooltipString:
          ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.BOOKED_ATTENDEES,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.ATTENDANCE_TITLE,
        statsNumber: `${Math.round(attendance_pct)}%`,
        Icon: ExlyPeopleIcon,
        color: "persian_blue",
        tooltipString: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.ATTENDANCE,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.FULL_ATTENDANCE_TAB,
        statsNumber: full_attendance.toString(),
        Icon: ExlyCheckCircleOutlineIcon,
        color: "fine_pine",
        tooltipString:
          ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.FULL_ATTENDANCE,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.ABSENT,
        statsNumber: absent_count.toString(),
        Icon: ExlyStarBorderIcon,
        color: "butterscotch_yellow",
        tooltipString: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.ABSENT,
      },
      {
        title: ZOOM_ATTENDANCE_CONSTANTS.LABELS.UNRECOGNIZED,
        statsNumber: unrecognized_count.toString(),
        Icon: ExlyHelpOutlineIcon,
        color: "coral_red",
        tooltipString: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOOLTIPS.UNRECOGNIZED,
      }
    );
  }

  const listingData = {
    title: listingName,
    cover_image: coverImage,
    type: listingType,
    uuid: listingUuid,
  };

  return (
    <div className="tw-px-4 sm:tw-px-0">
      <ListingDetails
        isDesktop={isDesktop}
        listing_data={listingData}
        loading={loading && !isSessionView}
        upsellCardsArray={statsCards}
        show_more={false}
        show_add_modal={false}
      />
    </div>
  );
};
